import Resources from '@/Resources.js';
import min from 'lodash/min';
import max from 'lodash/max';

export const secondsInDay = 86400;
export const secondsInHour = 3600;
export const secondsInMinute = 60;
export const millisecondsInDay = secondsInDay * 1000;
export const millisecondsInDayHour = secondsInHour * 1000;
export const millisecondsInMinute = secondsInMinute * 1000;

export const grammaticalCaseTypes = {
    nominative: 'nominative',
    accusative: 'accusative',
    // other cases can be added here if needed
};

export function getConversionResources(caseType = grammaticalCaseTypes.nominative) {
    const translationResources = {
        d: {
            [grammaticalCaseTypes.nominative]: Resources.ProjectHome.HumanizeDatePluralizedDay,
            [grammaticalCaseTypes.accusative]: Resources.ProjectHome.HumanizeDatePluralizedDayAccusativeCase,
        },
        h: {
            [grammaticalCaseTypes.nominative]: Resources.ProjectHome.HumanizeDatePluralizedHour,
            [grammaticalCaseTypes.accusative]: Resources.ProjectHome.HumanizeDatePluralizedHourAccusativeCase,
        },
        m: {
            [grammaticalCaseTypes.nominative]: Resources.ProjectHome.HumanizeDatePluralizedMinute,
            [grammaticalCaseTypes.accusative]: Resources.ProjectHome.HumanizeDatePluralizedMinuteAccusativeCase,
        },
        s: {
            [grammaticalCaseTypes.nominative]: Resources.ProjectHome.HumanizeDatePluralizedSecond,
            [grammaticalCaseTypes.accusative]: Resources.ProjectHome.HumanizeDatePluralizedSecondAccusativeCase,
        },
        ms: {
            [grammaticalCaseTypes.nominative]: Resources.ProjectHome.HumanizeDatePluralizedMillisecond,
            [grammaticalCaseTypes.accusative]: Resources.ProjectHome.HumanizeDatePluralizedMillisecondAccusativeCase,
        },
    };

    return [{
        unit: 'd', // 'day'
        factor: secondsInDay,
    },
    {
        unit: 'h', // 'hour'
        factor: secondsInHour,
    },
    {
        unit: 'm', // 'minute'
        factor: secondsInMinute,
    },
    {
        unit: 's', // 'second'
        factor: 1,
    },
    {
        unit: 'ms', // 'millisecond'
        factor: 0.001,
    }].map(c => ({ ...c, name: translationResources[c.unit][caseType] })); // add translation to each conversion resource
}
/**
 * Humanizes seconds to text.
 * @example
 * // humanize(60, 's') will create "60 seconds".
 * @example
 * // humanize(500, 'h') will return "0 hours" because the result it always rounded down .
 * @param {number} seconds - the time value in seconds.
 * @param {null|string} unit - can be one of following values: [null, 'd', 'h', 'm', 's']. If not specified the maximum the the highest supported unit is used.
 * @param {string} caseType - grammatical case to use for the unit name. This is used for languages like Polish
 * where the unit form changes by case, for example nominative "1 sekunda" vs accusative "1 sekundę".
 * @returns The time value in text format.
 */
export const humanize = (seconds, unit, caseType = grammaticalCaseTypes.nominative) => {
    let conversion = null;
    if (!unit) {
        const factorsDescending = getConversionResources(caseType)
            .map(c => c.factor)
            .sort((a, b) => b - a);
        const foundIndex = factorsDescending.findIndex(c => c <= seconds);
        const maximumSupportedUnitIndex = foundIndex === -1 ? factorsDescending.length - 1 : foundIndex;
        conversion = getConversionResources(caseType)[maximumSupportedUnitIndex];
    }
    else {
        conversion = getConversionResources(caseType).find(c => c.unit === unit);
    }

    const value = Math.floor(seconds / conversion.factor);
    const unitName = Resources.pluralize(value, conversion.name);
    return `${ value } ${ unitName }`;
};

// creates a string which contains humanized form with up to two parts.
// for example twoPartHumanize(61) will return "1 minute 1 second"
// if the second part is 0 then it's omitted. This means twoPartHumanize(60) will return "1 minute"
export const twoPartHumanize = (seconds, caseType = grammaticalCaseTypes.nominative) => {
    if (seconds < 0)
        throw new Error('seconds value must be greater or equal 0');

    const factorsDescending = getConversionResources(caseType)
        .map(c => c.factor)
        .sort((a, b) => b - a);
    const foundIndex = factorsDescending.findIndex(c => c <= seconds);
    const maximumSupportedUnitIndex = foundIndex === -1 ? factorsDescending.length - 1 : foundIndex;

    const firstPartConversion = getConversionResources(caseType)[maximumSupportedUnitIndex];
    const humanizedFirstPart = humanize(seconds, firstPartConversion.unit, caseType);
    const secondPartConversion = getConversionResources(caseType)[maximumSupportedUnitIndex + 1];
    return maximumSupportedUnitIndex < getConversionResources(caseType).length - 1 && seconds % firstPartConversion.factor >= secondPartConversion.factor
        ? Resources.format(
            Resources.ProjectHome.HumanizeDateTwoPartsLabel,
            humanizedFirstPart,
            humanize(seconds % firstPartConversion.factor, secondPartConversion.unit, caseType))
        : humanizedFirstPart;
};

export const threePartHumanize = (seconds, caseType = grammaticalCaseTypes.nominative) => {
    if (seconds < 0)
        throw new Error('seconds value must be greater or equal 0');

    const conversionResources = getConversionResources(caseType);
    const sortedFactors = conversionResources.map(c => c.factor).sort((a, b) => b - a);
    const foundIndex = sortedFactors.findIndex(f => f <= seconds);
    const firstIndex = foundIndex === -1 ? sortedFactors.length - 1 : foundIndex;

    const firstPart = conversionResources[firstIndex];
    const firstHumanized = humanize(seconds, firstPart.unit, caseType);
    let remainingSeconds = seconds % firstPart.factor;

    if (firstIndex >= conversionResources.length - 1 || remainingSeconds === 0) {
        return firstHumanized;
    }

    const secondPart = conversionResources[firstIndex + 1];
    const secondHumanized = humanize(remainingSeconds, secondPart.unit, caseType);
    remainingSeconds %= secondPart.factor;

    if (firstIndex + 1 >= conversionResources.length - 1 || remainingSeconds === 0) {
        return Resources.format(Resources.ProjectHome.HumanizeDateTwoPartsLabel, firstHumanized, secondHumanized);
    }

    const thirdPart = conversionResources[firstIndex + 2];
    const thirdHumanized = humanize(remainingSeconds, thirdPart.unit, caseType);

    return Resources.format(Resources.ProjectHome.HumanizeDateThreePartsLabel, firstHumanized, secondHumanized, thirdHumanized);
};

export function dateToISOString(date) {
    if (!(date instanceof Date))
        throw new Error('param should be instance of Date');

    const day = `0${ date.getDate() }`.slice(-2);
    const month = `0${ date.getMonth() + 1 }`.slice(-2);
    return `${ date.getFullYear() }-${ month }-${ day }`;
}

export function dateToISODateString(date) {
    if (!(date instanceof Date))
        throw new Error('param should be instance of Date');

    return date.toISOString().split('T')[0];
}

export function isValidDate(d) {
    return d instanceof Date && !isNaN(d);
}

export function parseValidDate(value) {
    if (value === null || value === undefined) {
        return null;
    }

    const date = new Date(value);
    return date instanceof Date && !isNaN(date) ? date : null;
}

export function humanizedTimeAgo(startDate, endDate = new Date(Date.now())) {
    const difference = endDate.getTime() - startDate.getTime();
    const seconds = Math.floor(difference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);
    const ago = (timeLabel) => Resources.format(Resources.ProjectHome.HumanizeDateAgoLabel, timeLabel);

    if (years > 0) {
        return `${ years } ${ ago(Resources.pluralize(years, Resources.ProjectHome.HumanizeDatePluralizedYear)) }`;
    }
    else if (months > 0) {
        return `${ months } ${ ago(Resources.pluralize(months, Resources.ProjectHome.HumanizeDatePluralizedMonth)) }`;
    }
    else if (days > 0) {
        return `${ days } ${ ago(Resources.pluralize(days, Resources.ProjectHome.HumanizeDatePluralizedDay)) }`;
    }
    else if (hours > 0) {
        return `${ hours } ${ ago(Resources.pluralize(hours, Resources.ProjectHome.HumanizeDatePluralizedHour)) }`;
    }
    else if (minutes > 0) {
        return `${ minutes } ${ ago(Resources.pluralize(minutes, Resources.ProjectHome.HumanizeDatePluralizedMinute)) }`;
    }
    else {
        return `${ seconds } ${ ago(Resources.pluralize(seconds, Resources.ProjectHome.HumanizeDatePluralizedSecond)) }`;
    }
}

export function monthsDifference(dateFrom, dateTo) {
    const fromMonth = dateFrom.getMonth();
    const toMonth = dateTo.getMonth();
    const fromYear = dateFrom.getFullYear();
    const toYear = dateTo.getFullYear();

    const diff = toMonth - fromMonth + 12 * (toYear - fromYear);

    return diff >= 0 ? diff : -diff;
}

export function monthsDifferenceUtc(dateFrom, dateTo) {
    const fromMonth = dateFrom.getUTCMonth();
    const toMonth = dateTo.getUTCMonth();
    const fromYear = dateFrom.getUTCFullYear();
    const toYear = dateTo.getUTCFullYear();

    const diff = toMonth - fromMonth + 12 * (toYear - fromYear);

    return diff >= 0 ? diff : -diff;
}

export function truncateDate(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function differenceInDays(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const diffTime = end.getTime() - start.getTime();
    return diffTime / millisecondsInDay;
}

export function differenceInCalendarDays(startDate, endDate, options = {}) {
    const { includeStartingDay = true } = options;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const startOfDayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const startOfDayEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    return Math.ceil((startOfDayEnd - startOfDayStart) / (1000 * 3600 * 24) + (includeStartingDay ? 1 : 0));
}

export function isLessThanGivenDaysUntil(date, days, options = {}) {
    const { mode = 'exact', includeToday = false } = options;
    const now = new Date();

    const diff = mode === 'calendar'
        ? differenceInCalendarDays(now, date, { includeStartingDay: includeToday })
        : differenceInDays(now, date);

    return diff <= days;
}

export function getEarlierDate(date1, date2) {
    return min([date1, date2]);
}

export function getLaterDate(date1, date2) {
    return max([date1, date2]);
}

export function addDays(date, days) {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + days);
    return newDate;
}

export function getNextFullHour(date) {
    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + 1);
    newDate.setMinutes(0, 0, 0);
    return newDate;
}

export function getUtcOffset() {
    const offsetInMinutes = new Date().getTimezoneOffset();
    const offsetInHours = Math.floor(Math.abs(offsetInMinutes) / 60);
    const sign = offsetInMinutes > 0 ? '-' : '+';
    return `UTC${sign}${offsetInHours.toString()}`;
}

export default {
    humanize,
    twoPartHumanize,
    isValidDate,
    getEarlierDate,
    getLaterDate,
};
